import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { academicService, pedagogyService } from '../services/api';
import { Card, Badge, Button, Spinner, EmptyState } from '../components/ui';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import useAuthStore from '../store/authStore';
import PedagogicalNoteModal from '../components/PedagogicalNoteModal';

const LANGUAGE_LABELS = { ar: 'العربية', fr: 'Français', en: 'English' };
const TEMPLATE_TONES = { classic: 'blue', modern: 'green', ministerial: 'purple' };

function TeacherDocuments() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { user } = useAuthStore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const openEdit = (doc) => {
    setEditingDocument(doc);
    setEditingModule(null);
    if (doc.module) {
      academicService.getModule(doc.module).then((res) => setEditingModule(res.data)).catch(() => setEditingModule(null));
    }
  };

  const fetchDocuments = useCallback(() => {
    setLoading(true);
    pedagogyService.listDocuments()
      .then((res) => {
        const data = res.data;
        setDocuments(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => addNotification(t('Failed to load your documents'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await pedagogyService.downloadDocument(doc.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.title || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addNotification(t('Failed to download the document'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!(await confirm(t('Delete {{name}}? This cannot be undone.', { name: doc.title || doc.session_title || t('this document') })))) return;
    setBusyId(doc.id);
    try {
      await pedagogyService.deleteDocument(doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      addNotification(t('Document deleted'), 'success');
    } catch (error) {
      addNotification(t('Failed to delete the document'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('My Documents')} subtitle={t('Your saved pedagogical notes, ready to download, edit, or remove')} />
        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : documents.length === 0 ? (
            <Card><EmptyState title={t('No documents yet')} description={t('Generate a teaching note from any of your courses and it will appear here.')} /></Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {documents.map((doc) => (
                <Card key={doc.id} className="p-5 flex flex-col gap-3">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-gray-900 truncate">{doc.title || doc.sessions?.[0]?.session_title || t('Untitled document')}</h3>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: doc.accent_color }} />
                    </div>
                    {doc.module_name && <p className="text-xs text-gray-400 truncate">{doc.module_name}</p>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge tone={TEMPLATE_TONES[doc.template] || 'blue'}>{t(doc.template)}</Badge>
                    <Badge tone="gray">{LANGUAGE_LABELS[doc.language] || doc.language}</Badge>
                    {doc.sessions?.length > 1 && (
                      <Badge tone="yellow">{t('{{count}} lessons', { count: doc.sessions.length })}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t('Updated')} {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(doc.updated_at))}
                  </p>
                  <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100">
                    <Button size="sm" variant="secondary" loading={busyId === doc.id} onClick={() => handleDownload(doc)}>
                      {t('Download')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => openEdit(doc)}>
                      {t('Edit')}
                    </Button>
                    <Button size="sm" variant="danger" loading={busyId === doc.id} onClick={() => handleDelete(doc)}>
                      {t('Delete')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      <PedagogicalNoteModal
        open={!!editingDocument}
        onClose={() => setEditingDocument(null)}
        existingDocument={editingDocument}
        module={editingModule}
        currentUser={user}
        onSaved={fetchDocuments}
      />
    </div>
  );
}

export default TeacherDocuments;
